import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { find, unionBy } from 'lodash';
import { first } from 'rxjs/operators';
export interface ISpaceQueryOptions {
  query: string;
  indexes: Array<string> | string;
  clearCache?: boolean;
  filters?: string;
  perPage?: number;
  facets?: any;
}

export class SpaceSearchService {
  static $inject = ['$q', 'ReportModel', 'SearchClientService', 'ModeCollection', 'FeatureFlagsFacade'];
  owner: any;
  isLimitReportPropertyList = false;

  constructor(
    private $q,
    private ReportModel,
    private _searchClientService,
    private ModeCollection,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.featureFlagsFacade
      .asObservable(FeatureFlag.LimitReportPropertyList)
      .pipe(first())
      .subscribe((isLimitReportPropertyList) => (this.isLimitReportPropertyList = isLimitReportPropertyList));
  }

  setOwner(owner) {
    this.owner = owner;
  }

  // TODO: this is kind of a hack until models are ported and can be used easily by redux
  // for now we have to handle that another way so this callback function is being
  // used to encapsulate that model logic
  handleSearchResponse = (response) => {
    const ids = this.getIdsFromSearch(response.results[0].hits);
    return this.handleIdsResponse(ids, response);
  };

  handleSearchResponseWithSpaces = (response) => {
    const ids = this.getIdsFromSearch(response.results[0].hits);
    let reports = [];
    if (this.isLimitReportPropertyList) {
      reports.push({
        only: 'token,id,name,description,created_at,updated_at,published_at,edited_at,type,last_saved_at,archived,shared,last_run_at,query_count,chart_count,runs_count,schedules_count,view_count,web_preview_image',
      });
    }

    reports = reports.concat(['creator', 'star', 'new_star', 'new_space_link', { space: 'creator' }]);
    return this.handleIdsResponse(ids, response, {
      reports: reports,
    });
  };

  handleIdsResponse = (ids, response?, includes?) =>
    this.buildReportListFromIds(ids, includes).then((reports) => {
      let nextPage = null;
      const spaceReportPins = response ? response.pins : null;
      if (
        response &&
        response.results &&
        response.results[0] &&
        response.results[0].page < response.results[0].nbPages - 1
      ) {
        nextPage = response.results[0].page + 1;
      }
      // setup saved pin state on reports that are pinned to this space
      reports.forEach((report) => {
        const pin = find(response.pins, (pin: any) => pin.report_id === report.id);
        if (pin) {
          report.savedSpaceReportPin = pin;
        } else {
          report.savedSpaceReportPin = null;
        }
      });
      return { nextPage, reports };
    });

  handleArchivedResponse = (options: ISpaceQueryOptions, searchResults) => {
    if (searchResults.reports.length) {
      return searchResults;
    }
    // also check archived and set a flag to show that archived reports exist in the space
    return this.querySpace(options).then((archivedResults) => {
      if (archivedResults.reportLastEdited && archivedResults.reportLastEdited.length) {
        searchResults.archivedReports = true;
      }
      return searchResults;
    });
  };

  querySpace(options: ISpaceQueryOptions) {
    return this._searchClientService.query(options);
  }

  querySpaceAndPinnedReports(options: ISpaceQueryOptions, space: any, merge = false) {
    const queries = [this._searchClientService.query(options)];
    if (space && space._resource.hasLink('space_report_pins')) {
      queries.push(space.spaceReportPins({ refresh: true }).then(this.ModeCollection.elements));
    }
    return this.$q.all(queries).then(this.mergeSpaceAndPinResults.bind(this, merge));
  }

  mergeSpaceAndPinResults(doMerge = false, searchResults) {
    const pins = searchResults.length > 1 ? searchResults[1] : [];
    if (doMerge) {
      searchResults[0].results[0].hits = unionBy(
        searchResults[0].results[0].hits,
        pins,
        (report: any) => report.report_id || report.id || report.objectID
      );
    }
    return {
      pins: pins,
      results: searchResults[0].results,
    };
  }

  reportModelsFromResponse(response) {
    return this.$q.all(
      response._embedded.reports.map((report) => {
        const reportModel = new this.ReportModel.model(report);
        return this.ReportModel.putLocal(reportModel);
      })
    );
  }

  buildReportListFromIds(response, includes?) {
    const byIdsLink = 'by_ids_base_reports';
    const defaultIncludes = { reports: ['creator', 'star', 'new_star', 'new_space_link'] };
    if (response.ids && response.ids.length) {
      return this.owner._resource
        .links(byIdsLink)
        .$follow({ ids: response.ids, includes: includes || defaultIncludes })
        .then(this.reportModelsFromResponse.bind(this));
    } else {
      return this.$q.resolve([]);
    }
  }

  getIdsFromSearch(response) {
    const result = { ids: [] };

    if (response) {
      result.ids = response.map((report: any) => report.report_id || report.id || report.objectID);
    }
    return result;
  }
}
